import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    effect,
    inject,
    signal,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { UserStore } from '@core/services/user/user.store'
import { getUserId } from '@app/core/helpers/user.helper'
import { REGEX_PATTERNS } from '@app/core/constants/regex.constants'
import { PERMISSIONS } from '@app/core/constants/permissions.constants'
import { Subscription } from 'rxjs'
import { ShikshaKpiStripComponent } from '@features/home/shiksha-components/shiksha-kpi-strip/shiksha-kpi-strip.component'
import { ShikshaTeamContributionComponent } from '@features/home/shiksha-components/shiksha-team-contribution/shiksha-team-contribution.component'
import { ShikshaSalesFunnelComponent } from '@features/home/shiksha-components/shiksha-sales-funnel/shiksha-sales-funnel.component'
import { ShikshaRevenueByClientTypeComponent } from '@features/home/shiksha-components/shiksha-revenue-by-client-type/shiksha-revenue-by-client-type.component'
import { ShikshaDealsAtRiskComponent } from '@features/home/shiksha-components/shiksha-deals-at-risk/shiksha-deals-at-risk.component'
import { ShikshaPipelineRiskComponent } from '@features/home/shiksha-components/shiksha-pipeline-risk/shiksha-pipeline-risk.component'
import { SharedModule } from '@app/shared/shared.module'
import { ShikshaClientTypeRevenueService } from '@features/home/shiksha-components/services/shiksha-client-type-revenue.service'
import { ShikshaPipelineRiskService } from '@features/home/shiksha-components/services/shiksha-pipeline-risk.service'
import {
    buildShikshaNumberedStageFallbackLabel,
    SHIKSHA_DASHBOARD_API_PARAMS,
    SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT,
    SHIKSHA_CLIENT_TYPE_REVENUES,
    SHIKSHA_DASHBOARD_CURRENCY,
    SHIKSHA_DASHBOARD_TEXT,
    SHIKSHA_DASHBOARD_TOOLTIP_TEXT,
    SHIKSHA_DASHBOARD_STATE_TEXT,
    SHIKSHA_DEALS_AT_RISK_ITEMS,
    SHIKSHA_DASHBOARD_KPI_LABELS,
    SHIKSHA_KPI_ITEMS,
    SHIKSHA_SALES_FUNNEL_STAGES,
    SHIKSHA_TEAM_CONTRIBUTION_MEMBERS,
    SHIKSHA_FUNNEL_DEAL_WON_STAGE_CODE,
    SHIKSHA_FUNNEL_STAGE_LABEL_BY_CODE,
    SHIKSHA_DASHBOARD_SCOPE,
    parseShikshaReporteeScopeValue,
} from '@features/home/shiksha-components/constants/shiksha-dashboard.constants'
import { ShikshaDashboardFiltersService } from '@core/services/shiksha/shiksha-dashboard-filters.service'
import { ShikshaDashboardDataService } from '@core/services/shiksha/shiksha-dashboard-data.service'
import { PermissionService } from '@core/services/permission-service/permission-service'
import {
    ILostS3S4AggregationResponse,
    ILostStageSummary,
    IRevenueHealthMetricsResponse,
    IRevenueHealthTeamContributionRow,
    ISalesFunnelSnapshotsResponse,
    IShikshaClientTypeRevenue,
    IShikshaDealsAtRiskItem,
    IShikshaKpiItem,
    IShikshaSalesFunnelStage,
    IShikshaSalesFunnelStageValueOverrides,
    IShikshaTeamContributionMember,
} from '@app/features/home/models/shiksha-dashboard.model'
import type { IShikshaPipelineRiskSummaryView } from '@core/models/shiksha-dashboard.model'

@Component({
    selector: 'app-shiksha-coming-soon',
    imports: [
        ShikshaKpiStripComponent,
        ShikshaTeamContributionComponent,
        ShikshaSalesFunnelComponent,
        ShikshaDealsAtRiskComponent,
        ShikshaRevenueByClientTypeComponent,
        ShikshaPipelineRiskComponent,
        SharedModule,
    ],
    templateUrl: './shiksha-coming-soon.html',
    styleUrl: './shiksha-coming-soon.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShikshaComingSoonComponent implements OnDestroy {
    private readonly route = inject(ActivatedRoute)
    private readonly userStore = inject(UserStore)
    private readonly dashboardFilters = inject(ShikshaDashboardFiltersService)
    private readonly shikshaDashboardDataService = inject(ShikshaDashboardDataService)
    private readonly shikshaClientTypeRevenueService = inject(ShikshaClientTypeRevenueService)
    private readonly shikshaPipelineRiskService = inject(ShikshaPipelineRiskService)
    private readonly permissionService = inject(PermissionService)
    private revenueHealthSubscription: Subscription | null = null
    private clientTypeRevenueSubscription: Subscription | null = null
    private pipelineRiskSubscription: Subscription | null = null

    readonly kpiItems = signal<readonly IShikshaKpiItem[]>(SHIKSHA_KPI_ITEMS)
    readonly teamMembers = signal<readonly IShikshaTeamContributionMember[]>(
        SHIKSHA_TEAM_CONTRIBUTION_MEMBERS,
    )
    readonly salesFunnelStages = signal<readonly IShikshaSalesFunnelStage[]>(
        SHIKSHA_SALES_FUNNEL_STAGES,
    )
    readonly dealsAtRiskItems = signal<readonly IShikshaDealsAtRiskItem[]>(
        SHIKSHA_DEALS_AT_RISK_ITEMS,
    )
    readonly clientTypeRevenues = signal<readonly IShikshaClientTypeRevenue[]>(
        SHIKSHA_CLIENT_TYPE_REVENUES,
    )
    readonly pipelineRiskSummary = signal<IShikshaPipelineRiskSummaryView>({
        stages: [],
        onHoldLeadCount: 0,
    })
    readonly selectedScope = this.dashboardFilters.selectedScope
    readonly selectedYear = this.dashboardFilters.selectedYear
    readonly isDashboardLoading = this.dashboardFilters.isRefreshing
    readonly isTeamScopeSelected = signal<boolean>(true)
    readonly hasShikshaDashboardAccess = signal<boolean>(true)
    readonly dashboardStateText = SHIKSHA_DASHBOARD_STATE_TEXT

    constructor() {
        this.loadFinancialYears()

        effect(
            () => {
                this.hasShikshaDashboardAccess.set(this.canViewShikshaDashboard())
                void this.userStore.userData()?.user_id
                this.isTeamScopeSelected.set(this.getApiScope(this.selectedScope()) === 'team')
                this.selectedYear()
                this.dashboardFilters.refreshTick()
                this.loadRevenueHealthMetrics()
            },
            { allowSignalWrites: true },
        )
    }

    private loadFinancialYears(): void {
        this.shikshaDashboardDataService.fetchFinancialYears().subscribe((response) => {
            this.dashboardFilters.setAvailableFinancialYears(response?.data?.financialYears ?? [])
        })
    }

    private loadRevenueHealthMetrics(): void {
        if (!this.hasShikshaDashboardAccess()) {
            this.dashboardFilters.markRefreshComplete()
            return
        }

        this.dashboardFilters.markRefreshInProgress()
        const userIds = this.resolveUserIds()
        const financialYear = this.getFinancialYear(this.selectedYear())
        if (!financialYear) {
            this.clientTypeRevenues.set(SHIKSHA_CLIENT_TYPE_REVENUES)
            this.pipelineRiskSummary.set({ stages: [], onHoldLeadCount: 0 })
            this.dashboardFilters.markRefreshComplete()
            return
        }

        // Clear scope-specific widgets immediately so a fast forkJoin + refresh complete cannot
        // reveal previous View Scope data while the client-type API is still in flight (or empty).
        this.clientTypeRevenues.set(SHIKSHA_CLIENT_TYPE_REVENUES)
        this.dealsAtRiskItems.set(SHIKSHA_DEALS_AT_RISK_ITEMS)

        this.revenueHealthSubscription?.unsubscribe()
        const selectedApiScope = this.getApiScope(this.selectedScope())
        this.loadClientTypeRevenues(financialYear, userIds, selectedApiScope)
        this.loadPipelineRiskSummary(userIds, financialYear, selectedApiScope)
        this.revenueHealthSubscription = this.shikshaDashboardDataService
            .fetchRevenueHealthMetrics(financialYear, selectedApiScope, userIds)
            .subscribe(
                ({
                    kpiResponse,
                    contributionResponse,
                    salesFunnelResponse,
                    s3EstimateResponse,
                    lostS3S4Response,
                }) => {
                    const pipelineValue =
                        kpiResponse?.data?.target_pipeline ??
                        contributionResponse?.data?.target_pipeline ??
                        0
                    const teamContribution = contributionResponse?.data?.team_contribution ?? []
                    const salesFunnelStages = salesFunnelResponse?.data?.stages ?? []
                    const salesFunnelConversions = salesFunnelResponse?.data?.conversions
                    const realizedRevenue =
                        contributionResponse?.data?.deal_won_value ??
                        teamContribution.reduce(
                            (sum, member) =>
                                sum + (member.dealWonValue ?? member.deal_won_value ?? 0),
                            0,
                        )
                    const healthPipelineDealWon =
                        contributionResponse?.data?.deal_won_value ??
                        kpiResponse?.data?.deal_won_value ??
                        0
                    const s3EstimateData = s3EstimateResponse?.data
                    const s3EstimateTotalRaw =
                        s3EstimateData?.totalEstimateDealValue ??
                        s3EstimateData?.total_estimate_deal_value
                    const funnelValueOverrides: IShikshaSalesFunnelStageValueOverrides = {
                        healthPipelineDealWonValue: Number.isFinite(healthPipelineDealWon)
                            ? healthPipelineDealWon
                            : 0,
                    }
                    if (s3EstimateTotalRaw != null && Number.isFinite(Number(s3EstimateTotalRaw))) {
                        funnelValueOverrides.s3EstimateTotalValue = Number(s3EstimateTotalRaw)
                    }

                    this.kpiItems.set(this.getMappedKpiItems(pipelineValue, realizedRevenue))
                    this.teamMembers.set(this.getMappedTeamMembers(teamContribution, pipelineValue))
                    this.salesFunnelStages.set(
                        this.getMappedSalesFunnelStages(
                            salesFunnelStages,
                            salesFunnelConversions,
                            funnelValueOverrides,
                        ),
                    )
                    this.dealsAtRiskItems.set(
                        this.getMappedDealsAtRiskItems(salesFunnelStages, lostS3S4Response),
                    )
                    this.dashboardFilters.markRefreshComplete()
                },
            )
    }

    ngOnDestroy(): void {
        this.revenueHealthSubscription?.unsubscribe()
        this.revenueHealthSubscription = null
        this.clientTypeRevenueSubscription?.unsubscribe()
        this.clientTypeRevenueSubscription = null
        this.pipelineRiskSubscription?.unsubscribe()
        this.pipelineRiskSubscription = null
    }

    private loadClientTypeRevenues(
        financialYear: number,
        userIds: string | null,
        scope: 'my' | 'team',
    ): void {
        this.clientTypeRevenueSubscription?.unsubscribe()
        this.clientTypeRevenueSubscription = this.shikshaClientTypeRevenueService
            .getClientTypeRevenues(financialYear, userIds, scope)
            .subscribe({
                next: (revenues) => {
                    this.clientTypeRevenues.set(revenues)
                },
                error: () => {
                    this.clientTypeRevenues.set(SHIKSHA_CLIENT_TYPE_REVENUES)
                },
            })
    }

    private loadPipelineRiskSummary(
        userId: string | null,
        financialYear: number,
        scope: 'my' | 'team',
    ): void {
        const primaryUserId = userId?.split(',')[0]?.trim() || null
        if (!primaryUserId) {
            this.pipelineRiskSummary.set({ stages: [], onHoldLeadCount: 0 })
            return
        }

        this.pipelineRiskSubscription?.unsubscribe()
        this.pipelineRiskSubscription = this.shikshaPipelineRiskService
            .getPipelineRiskSummary(primaryUserId, financialYear, scope)
            .subscribe({
                next: (summary) => this.pipelineRiskSummary.set(summary),
                error: () => this.pipelineRiskSummary.set({ stages: [], onHoldLeadCount: 0 }),
            })
    }

    private resolveUserIds(): string | null {
        const queryUserIds = this.route.snapshot.queryParamMap.get(
            SHIKSHA_DASHBOARD_API_PARAMS.USER_IDS,
        )
        if (queryUserIds?.trim()) {
            return queryUserIds
        }

        const queryUserId = this.route.snapshot.queryParamMap.get(
            SHIKSHA_DASHBOARD_API_PARAMS.USER_ID,
        )
        if (queryUserId?.trim()) {
            return queryUserId
        }

        const scopeReporteeId = parseShikshaReporteeScopeValue(this.selectedScope())
        if (scopeReporteeId) {
            return scopeReporteeId
        }

        const jwtUserId = getUserId(this.userStore)
        return jwtUserId?.trim() ? jwtUserId : null
    }

    private canViewShikshaDashboard(): boolean {
        return Boolean(
            this.permissionService.getPermissionByName(PERMISSIONS.CREATE_CLIENT_SHIKSHA) ||
                this.permissionService.getPermissionByName(PERMISSIONS.VIEW_CLIENT_SHIKSHA) ||
                this.permissionService.getPermissionByName(PERMISSIONS.VIEW_DEAL_SHIKSHA) ||
                this.permissionService.getPermissionByName(PERMISSIONS.CREATE_DEAL_SHIKSHA),
        )
    }

    private getMappedKpiItems(
        targetPipeline: number,
        realizedRevenue: number,
    ): readonly IShikshaKpiItem[] {
        const gapToTarget = Math.max(targetPipeline - realizedRevenue, 0)
        const coveragePercent = targetPipeline > 0 ? (realizedRevenue / targetPipeline) * 100 : 0
        const coverageRatioLabel = `${(coveragePercent / 100).toFixed(2)}x`
        const receivedPercentLabel =
            targetPipeline > 0
                ? `${Math.round(coveragePercent)}${SHIKSHA_DASHBOARD_TEXT.percentOfTargetSuffix}`
                : SHIKSHA_DASHBOARD_TEXT.zeroPercentOfTarget
        const gapPercentLabel =
            targetPipeline > 0
                ? `${Math.round((gapToTarget / targetPipeline) * 100)}${SHIKSHA_DASHBOARD_TEXT.percentOfTargetSuffix}`
                : SHIKSHA_DASHBOARD_TEXT.zeroPercentOfTarget

        return [
            {
                label: SHIKSHA_DASHBOARD_KPI_LABELS.PIPELINE_TARGET,
                value: this.formatInrAmount(targetPipeline),
                tooltipText: SHIKSHA_DASHBOARD_TOOLTIP_TEXT.targetPipeline,
                iconType: 'pipeline',
            },
            {
                label: SHIKSHA_DASHBOARD_KPI_LABELS.DEAL_WON_VALUE,
                value: this.formatInrAmount(realizedRevenue),
                subLabel: receivedPercentLabel,
                tooltipText: SHIKSHA_DASHBOARD_TOOLTIP_TEXT.receivedRevenue,
                iconType: 'revenue',
            },
            {
                label: SHIKSHA_DASHBOARD_KPI_LABELS.PIPELINE_COVERAGE,
                value: coverageRatioLabel,
                subLabel: SHIKSHA_DASHBOARD_KPI_LABELS.PIPELINE_COVERAGE_SUBLABEL,
                tooltipText: SHIKSHA_DASHBOARD_TOOLTIP_TEXT.coverageRatio,
                iconType: 'coverage',
                coveragePercent: Math.max(0, Math.min(coveragePercent, 100)),
            },
            {
                label: SHIKSHA_DASHBOARD_KPI_LABELS.GAP_TO_TARGET,
                value: this.formatInrAmount(gapToTarget),
                subLabel: gapPercentLabel,
                tooltipText: SHIKSHA_DASHBOARD_TOOLTIP_TEXT.gapToTarget,
                iconType: 'gap',
            },
        ]
    }

    private getMappedTeamMembers(
        teamContribution: NonNullable<IRevenueHealthMetricsResponse['data']>['team_contribution'],
        _targetPipeline: number,
    ): readonly IShikshaTeamContributionMember[] {
        if (!teamContribution?.length) {
            return SHIKSHA_TEAM_CONTRIBUTION_MEMBERS
        }

        const sorted = [...teamContribution].sort((a, b) => {
            const ra = a.rank ?? Number.MAX_SAFE_INTEGER
            const rb = b.rank ?? Number.MAX_SAFE_INTEGER
            return ra - rb
        })

        return sorted.map((member, index) => {
            const displayName = this.resolveTeamMemberName(member, index)
            const contributionPercent =
                member.contributionPercent ?? member.contribution_percent ?? 0
            const dealWonValue = member.dealWonValue ?? member.deal_won_value ?? 0
            const s3EstimatedAmount =
                member.s3EstimatedAmount ??
                member.s3_estimated_amount ??
                member.estimateValue ??
                member.estimate_value ??
                member.estimated_value ??
                member.estimatedValue ??
                0
            const winRateRaw = member.winRate ?? member.win_rate
            const winRatePercent =
                winRateRaw != null && Number.isFinite(Number(winRateRaw))
                    ? Number(winRateRaw)
                    : null
            const contributionRank = member.rank ?? index + 1

            return {
                name: displayName,
                closedRevenue: this.formatInrAmount(dealWonValue),
                conversion: '',
                pipeline: `${SHIKSHA_DASHBOARD_KPI_LABELS.TEAM_PIPELINE_PREFIX}: ${this.formatInrAmount(s3EstimatedAmount)}`,
                teamRevenuePercent: Number(Number(contributionPercent).toFixed(1)),
                contributionRank,
                winRatePercent,
            }
        })
    }

    private resolveTeamMemberName(
        member: IRevenueHealthTeamContributionRow,
        index: number,
    ): string {
        const userName = (member.user_name ?? member.userName ?? '').trim()
        if (userName) {
            return userName
        }

        const firstName = (member.firstName ?? member.first_name ?? '').trim()
        const lastName = (member.lastName ?? member.last_name ?? '').trim()
        if (firstName && lastName) {
            return `${this.toTitleCase(firstName)} ${this.toTitleCase(lastName)}`
        }

        const apiNameCandidates = [member.name, member.fullName, member.full_name, member.userName]
        const firstValidApiName = apiNameCandidates.find(
            (candidate) => typeof candidate === 'string' && candidate.trim().length > 0,
        )
        if (firstValidApiName) {
            return this.toTitleCase(firstValidApiName.trim())
        }

        const rawUserId = (member.userId ?? member.user_id ?? '').trim()
        if (rawUserId) {
            return rawUserId
        }

        return `${SHIKSHA_DASHBOARD_TEXT.memberFallbackPrefix} ${index + 1} ${SHIKSHA_DASHBOARD_TEXT.memberFallbackSuffix}`
    }

    private toTitleCase(value: string): string {
        return value
            .split(/\s+/)
            .filter(Boolean)
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
            .join(' ')
    }

    private getMappedSalesFunnelStages(
        stages: NonNullable<ISalesFunnelSnapshotsResponse['data']>['stages'],
        conversions?: NonNullable<ISalesFunnelSnapshotsResponse['data']>['conversions'],
        valueOverrides?: IShikshaSalesFunnelStageValueOverrides,
    ): readonly IShikshaSalesFunnelStage[] {
        const conversionFromSummaryByStageCode: Partial<
            Record<
                string,
                (
                    conversionSummary: NonNullable<
                        ISalesFunnelSnapshotsResponse['data']
                    >['conversions'],
                ) => number | null | undefined
            >
        > = {
            // Show conversion for each stage as "this stage -> next stage".
            // Example: S2 row should display S2->S3, not S1->S2.
            S0: (conversionSummary) =>
                conversionSummary?.s1FromS0Pct ?? conversionSummary?.s0ToS1Pct,
            S1: (conversionSummary) =>
                conversionSummary?.s2FromS1Pct ?? conversionSummary?.s1ToS2Pct,
            S2: (conversionSummary) =>
                conversionSummary?.s3FromS2Pct ?? conversionSummary?.s2ToS3Pct,
            S3: (conversionSummary) =>
                conversionSummary?.s4FromS3Pct ?? conversionSummary?.s3ToS4Pct,
            S4: (conversionSummary) =>
                conversionSummary?.s5FromS4Pct ?? conversionSummary?.s4ToS5Pct,
        }
        if (!stages?.length) {
            return SHIKSHA_SALES_FUNNEL_STAGES
        }

        return stages
            .map((stage, index) => {
                const stageLeadCount =
                    stage.leads_count ?? stage.count ?? stage.totalDeals ?? stage.total_deals ?? 0
                const stageCode = stage.stageCode ?? stage.stage_code ?? stage.code ?? `S${index}`
                const normalizedStageCode = stageCode.trim().toUpperCase()
                const canonicalStageLabel = SHIKSHA_FUNNEL_STAGE_LABEL_BY_CODE[normalizedStageCode]
                const numberedFallback = buildShikshaNumberedStageFallbackLabel(index)
                const apiStageLabel =
                    stage.stageLabel ??
                    stage.stage_label ??
                    stage.stage_name ??
                    stage.name ??
                    numberedFallback
                const stageLabel =
                    canonicalStageLabel ??
                    SHIKSHA_SALES_FUNNEL_STAGES.find((item) => item.stageCode === stageCode)
                        ?.stageLabel ??
                    (apiStageLabel || numberedFallback)
                const conversionFromSummary =
                    conversionFromSummaryByStageCode[stageCode]?.(conversions) ?? null
                const conversionToNext =
                    stage.conversionToNext ??
                    stage.conversion_to_next ??
                    conversionFromSummary ??
                    (stageCode === 'S0' ? 0 : undefined) ??
                    0
                const isDealWonStage = stageCode === SHIKSHA_FUNNEL_DEAL_WON_STAGE_CODE
                const lostToNextCount = isDealWonStage
                    ? 0
                    : (stage.lostToNextCount ?? stage.lost_to_next_count ?? stage.lost ?? 0)
                const movedToNextCount = isDealWonStage
                    ? 0
                    : Number.isFinite(
                            (stage.movedToNextCount ?? stage.moved_to_next_count) as number,
                        )
                      ? ((stage.movedToNextCount ?? stage.moved_to_next_count) as number)
                      : Number.isFinite(stage.success ?? NaN)
                        ? (stage.success as number)
                        : 0
                const dropOffToNext =
                    stage.dropOffToNext ??
                    stage.drop_off_to_next ??
                    (stageLeadCount > 0 ? (lostToNextCount / stageLeadCount) * 100 : 0)
                const relativeBarPercent =
                    stage.relativeBarPercent ?? stage.relative_bar_percent ?? 0
                const resolvedWonCount = isDealWonStage ? stageLeadCount : 0
                const resolvedInProgressCount = isDealWonStage
                    ? 0
                    : Number.isFinite((stage.inProgress ?? stage.in_progress) as number)
                      ? ((stage.inProgress ?? stage.in_progress) as number)
                      : 0
                const resolvedCount = Math.max(
                    Number.isFinite(stageLeadCount) ? stageLeadCount : 0,
                    Math.max(movedToNextCount, 0) +
                        Math.max(resolvedInProgressCount, 0) +
                        Math.max(lostToNextCount, 0),
                )
                const dealWonValueFromSnapshot =
                    stage.dealWonValue ??
                    stage.deal_won_value ??
                    stage.value ??
                    stage.dealValue ??
                    stage.deal_value ??
                    0
                let resolvedDealWonValue = Number.isFinite(dealWonValueFromSnapshot)
                    ? dealWonValueFromSnapshot
                    : 0

                if (valueOverrides && normalizedStageCode === 'S4') {
                    const s3Total = valueOverrides.s3EstimateTotalValue
                    if (s3Total != null && Number.isFinite(s3Total)) {
                        resolvedDealWonValue = s3Total
                    }
                }
                if (valueOverrides && normalizedStageCode === SHIKSHA_FUNNEL_DEAL_WON_STAGE_CODE) {
                    const healthWon = valueOverrides.healthPipelineDealWonValue
                    resolvedDealWonValue =
                        healthWon != null && Number.isFinite(healthWon) ? healthWon : 0
                }

                return {
                    stageCode,
                    stageLabel,
                    count: resolvedCount,
                    dealWonValue: resolvedDealWonValue,
                    wonCount: resolvedWonCount,
                    inProgressCount: resolvedInProgressCount,
                    movedToNextCount: Math.max(movedToNextCount, 0),
                    conversionToNext: Number.isFinite(conversionToNext) ? conversionToNext : 0,
                    dropOffToNext: Number.isFinite(dropOffToNext) ? dropOffToNext : 0,
                    lostToNextCount: Number.isFinite(lostToNextCount) ? lostToNextCount : 0,
                    relativeBarPercent: Number.isFinite(relativeBarPercent)
                        ? relativeBarPercent
                        : 0,
                }
            })
            .sort((a, b) => this.getStageOrder(a.stageCode) - this.getStageOrder(b.stageCode))
    }

    private getMappedDealsAtRiskItems(
        stages: NonNullable<ISalesFunnelSnapshotsResponse['data']>['stages'],
        lostAggregationResponse: ILostS3S4AggregationResponse | null,
    ): readonly IShikshaDealsAtRiskItem[] {
        const lostByCode = new Map<string, number>()
        if (stages?.length) {
            for (const [index, stage] of stages.entries()) {
                const stageCode = (stage.stageCode ?? stage.stage_code ?? stage.code ?? `S${index}`)
                    .trim()
                    .toUpperCase()
                const lostDeals = Math.max(
                    stage.lostToNextCount ?? stage.lost_to_next_count ?? stage.lost ?? 0,
                    0,
                )
                lostByCode.set(stageCode, lostDeals)
            }
        }

        const lostValueDisplayByCode = new Map<string, string>()
        let s3AggregationDealCount: number | undefined
        let s4AggregationDealCount: number | undefined
        if (lostAggregationResponse?.success && lostAggregationResponse.data) {
            const d = lostAggregationResponse.data
            const s3 = d.s3Lost ?? d.s3_lost
            const s4 = d.s4Lost ?? d.s4_lost
            s3AggregationDealCount = this.getLostAggregationDealCount(s3)
            s4AggregationDealCount = this.getLostAggregationDealCount(s4)
            const s4Display = this.formatLostAggregationValueDisplay(s4)
            if (s4Display != null) {
                lostValueDisplayByCode.set('S4', s4Display)
            }
        }

        return ['S1', 'S2', 'S3', 'S4'].map((stageCode) => {
            const funnelLost = lostByCode.get(stageCode) ?? 0
            let aggregationLost: number | undefined
            if (stageCode === 'S3') {
                aggregationLost = s3AggregationDealCount
            } else if (stageCode === 'S4') {
                aggregationLost = s4AggregationDealCount
            }
            const lostDeals = aggregationLost !== undefined ? aggregationLost : funnelLost

            return {
                stageCode,
                lostDeals,
                totalLostDealValueDisplay: lostValueDisplayByCode.get(stageCode),
            }
        })
    }

    private getLostAggregationDealCount(block: ILostStageSummary | undefined): number | undefined {
        if (block == null) {
            return undefined
        }
        const raw = block.dealCount ?? block.deal_count
        if (raw == null) {
            return undefined
        }
        const n = Number(raw)
        if (!Number.isFinite(n)) {
            return undefined
        }
        return Math.max(Math.trunc(n), 0)
    }

    private formatLostAggregationValueDisplay(block: ILostStageSummary | undefined): string | null {
        if (block == null) {
            return null
        }
        const raw = block.totalLostDealValue ?? block.total_lost_deal_value ?? 0
        const n = Number(raw)
        return this.formatInrCompactValue(Number.isFinite(n) ? n : 0)
    }

    private getMappedClientTypeRevenues(
        rows: NonNullable<IRevenueHealthMetricsResponse['data']>['client_type_revenue'],
    ): readonly IShikshaClientTypeRevenue[] {
        if (!rows?.length) {
            return SHIKSHA_CLIENT_TYPE_REVENUES
        }

        const normalizedRows = rows
            .map((row) => {
                const clientTypeLabel = row.clientType ?? row.client_type ?? row.label ?? ''
                const clientTypeAmount = row.dealWonValue ?? row.deal_won_value ?? row.value ?? 0
                const sharePercent =
                    row.sharePercent ??
                    row.share_percent ??
                    row.contributionPercent ??
                    row.contribution_percent ??
                    0

                return {
                    label: clientTypeLabel.trim(),
                    amount: Number.isFinite(clientTypeAmount) ? clientTypeAmount : 0,
                    sharePercent: Number.isFinite(sharePercent) ? sharePercent : 0,
                }
            })
            .filter((row) => row.label.length > 0)

        if (!normalizedRows.length) {
            return SHIKSHA_CLIENT_TYPE_REVENUES
        }

        const totalAmount = normalizedRows.reduce((sum, row) => sum + row.amount, 0)

        return normalizedRows.map((row) => {
            const fallbackShare = totalAmount > 0 ? (row.amount / totalAmount) * 100 : 0
            const resolvedShare = row.sharePercent != null ? row.sharePercent : fallbackShare

            return {
                label: row.label,
                valuePrimary: this.formatInrCompactValue(row.amount),
                sharePercent: Number(resolvedShare.toFixed(1)),
            }
        })
    }

    private getFinancialYear(yearLabel: string | null | undefined): number | null {
        if (!yearLabel) {
            return null
        }
        const yearPart = yearLabel.split('-')[0]
        const parsedYear = Number.parseInt(yearPart, 10)
        return Number.isFinite(parsedYear) ? parsedYear : null
    }

    private getApiScope(scopeLabel: string): 'my' | 'team' {
        if (parseShikshaReporteeScopeValue(scopeLabel)) {
            return SHIKSHA_DASHBOARD_SCOPE.MY
        }
        return scopeLabel === SHIKSHA_DASHBOARD_SCOPE.MYSELF
            ? SHIKSHA_DASHBOARD_SCOPE.MY
            : SHIKSHA_DASHBOARD_SCOPE.TEAM
    }

    private getStageOrder(stageCode: string): number {
        const normalizedStageCode = stageCode.trim().toUpperCase()
        const stageCodeMatch = normalizedStageCode.match(REGEX_PATTERNS.SALES_FUNNEL_STAGE_CODE)
        if (!stageCodeMatch) {
            return Number.MAX_SAFE_INTEGER
        }
        return Number.parseInt(stageCodeMatch[1], 10)
    }

    private formatInrAmount(value: number): string {
        if (!Number.isFinite(value)) {
            return SHIKSHA_DASHBOARD_CURRENCY.INR_ZERO
        }

        const symbol = SHIKSHA_DASHBOARD_TEXT.currencySymbolRupee
        const fmt = SHIKSHA_CLIENT_TYPE_REVENUE_FORMAT

        if (Math.abs(value) >= fmt.croreThreshold) {
            return `${symbol}${(value / fmt.croreDivisor).toFixed(2)} ${fmt.croreUnit}`
        }

        if (Math.abs(value) >= fmt.lakhThreshold) {
            return `${symbol}${(value / fmt.lakhDivisor).toFixed(2)} ${fmt.lakhUnit}`
        }

        return `${symbol}${Math.round(value).toLocaleString(fmt.localeIndianEnglish)}`
    }

    private formatInrCompactValue(value: number): string {
        return this.formatInrAmount(value)
    }
}
